import { Fragment } from 'react';

import { lottieAnimations } from '@backrs_ui/common-ui/src/assets/lottieAnimations';
import { CheckboxGroup } from '@backrs_ui/common-ui/src/designSystem/checkbox/CheckboxGroup';
import { AppIcons } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import { Box, Button, Divider, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import Lottie from 'lottie-react';
import { useForm, FormProvider, Controller, UseFormReturn } from 'react-hook-form';

import { BackrsFormAutoComplete } from './BackrsFormAutoComplete';
import { BackrsFormTextField } from './BackrsFormTextField';
import { OtpInput } from './OtpInput';
import { BackrsFormField, BackrsFormValues } from './types';

type FormButtonProps = Readonly<{
  isLoading?: boolean;
  methodsToUse: UseFormReturn<BackrsFormValues>;
  isCheckOrRadioGroup: (field: BackrsFormField) => boolean | undefined;
  buttonLabel: string;
  fields: BackrsFormField[];
  buttonId?: string;
}>;

const FormButton = ({
  isLoading,
  methodsToUse,
  isCheckOrRadioGroup,
  buttonLabel,
  fields,
  buttonId,
}: FormButtonProps) => (
  <Box sx={buttonContainerStyle}>
    <Button
      type="submit"
      fullWidth
      hierarchy="Primary"
      size="lg"
      id={buttonId}
      data-ph-capture-attribute-name={buttonId}
      icon={isLoading ? 'Leading' : 'Default'}
      disabled={(!methodsToUse.formState.isValid && isCheckOrRadioGroup(fields[0])) || isLoading}
      endIcon={
        isLoading ? (
          <Box sx={loadingSpinnerStyle}>
            <Lottie animationData={lottieAnimations.dotsDotting} loop />
          </Box>
        ) : undefined
      }
    >
      {buttonLabel}
    </Button>
  </Box>
);

export type BackrsFormProps = Readonly<{
  fields: BackrsFormField[];
  onSubmit: (data: BackrsFormValues) => void;
  defaultValues?: BackrsFormValues;
  buttonLabel?: string;
  buttonId?: string;
  children?: React.ReactNode;
  methods?: UseFormReturn<BackrsFormValues>;
  isLoading?: boolean;
  variant?: 'default' | 'otp';
}>;

export const BackrsForm = ({
  fields,
  onSubmit,
  defaultValues,
  buttonLabel,
  buttonId,
  children,
  methods,
  isLoading,
  variant = 'default',
}: BackrsFormProps) => {
  const defaultMethods = useForm<BackrsFormValues>({ defaultValues });
  const methodsToUse = methods || defaultMethods;

  const handleSubmit = methodsToUse.handleSubmit((data: BackrsFormValues) => {
    onSubmit(data);
  });

  const groupedFields: Record<string, BackrsFormField[]> = {};

  fields.forEach((field) => {
    const groupKey = field.group || `__unique_${field.name}`;

    if (!groupedFields[groupKey]) {
      groupedFields[groupKey] = [];
    }

    groupedFields[groupKey].push(field);
  });

  const isCheckOrRadioGroup = (field: BackrsFormField) => {
    return (
      field.selectOptions &&
      (field.selectOptions.type === 'MULTIPLE' ||
        (field.selectOptions.type === 'SINGLE' && !field.selectOptions.renderAsSelect))
    );
  };

  const renderFormButton = (label: string) => (
    <FormButton
      isLoading={isLoading}
      methodsToUse={methodsToUse}
      isCheckOrRadioGroup={isCheckOrRadioGroup}
      buttonLabel={label}
      fields={fields}
      buttonId={buttonId}
    />
  );

  return (
    <FormProvider {...methodsToUse}>
      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        mt={'20px'}
        width="100%"
        gap={'16px'}
        display="flex"
        flexDirection="column"
      >
        {Object.entries(groupedFields).map(([groupKey, group], index) => {
          const isSingleFieldGroup = group.length === 1 && group[0].group === undefined;

          // Check if any fields in this group should be visible
          const values = methodsToUse.watch();
          const hasVisibleFields = group.some((field) => {
            const shouldShow = field.showIf ? field.showIf(values) : true;

            return shouldShow && !field.hidden;
          });

          // Render hidden fields outside of the group container to preserve form data
          const hiddenFields = group.map((field) => {
            const shouldShow = field.showIf ? field.showIf(values) : true;

            if (!shouldShow) {
              return null;
            }

            if (field.hidden) {
              return (
                <Controller
                  key={`${groupKey}-${field.id}`}
                  name={field.name || ''}
                  control={methodsToUse.control}
                  rules={field.rules}
                  defaultValue={field.defaultValue}
                  render={() => <></>} // Don't render any UI
                />
              );
            }

            return null;
          });

          // If no fields in this group are visible, only render hidden fields
          if (!hasVisibleFields) {
            return <Fragment key={groupKey}>{hiddenFields}</Fragment>;
          }

          return (
            <Box
              key={groupKey}
              display={isSingleFieldGroup ? 'block' : 'flex'}
              sx={isSingleFieldGroup ? {} : groupContainerStyle}
            >
              {group.map((field) => {
                const values = methodsToUse.watch();
                const shouldShow = field.showIf ? field.showIf(values) : true;

                if (!shouldShow) {
                  return null;
                }

                const isDisabled =
                  field.disabledBy &&
                  ((!!field.disabledBy?.disableWhenTruthy &&
                    !!methodsToUse.getValues(field.disabledBy.field)) ||
                    field.disabledBy.values.includes(
                      methodsToUse.getValues(field.disabledBy.field),
                    ));

                // Skip hidden fields as they're handled at the group level
                if (field.hidden) {
                  return null;
                }

                return (
                  <Fragment key={`${groupKey}-${field.id}`}>
                    <Controller
                      name={field.name || ''}
                      control={methodsToUse.control}
                      rules={field.rules}
                      disabled={isDisabled}
                      render={({ field: controllerField, fieldState: { error } }) => {
                        const isSelectDropdown =
                          field.selectOptions && field.selectOptions.renderAsSelect;

                        const isMultiple = field.selectOptions?.type === 'MULTIPLE';

                        if (isSelectDropdown) {
                          const options: { label: string; value: string; icon?: AppIcons }[] =
                            field.selectOptions?.options || [];

                          const formValue = controllerField.value;

                          const autoCompleteOptions =
                            field.autoCompleteEndpoint && Array.isArray(formValue)
                              ? [...options, ...formValue.map((value) => ({ label: value, value }))]
                              : options;

                          const autoCompleteWithDuplicatesRemoved = autoCompleteOptions.filter(
                            (option, index, self) =>
                              index === self.findIndex((t) => t.value === option.value),
                          );

                          return (
                            <BackrsFormAutoComplete
                              isMultiple={isMultiple}
                              autoCompleteOptions={autoCompleteWithDuplicatesRemoved}
                              field={field}
                              options={options}
                              controllerField={controllerField}
                              groupedFields={groupedFields}
                              index={index}
                              error={error}
                              groupKey={groupKey}
                              isDisabled={isDisabled || false}
                            />
                          );
                        }

                        const isRadioGroup =
                          field.selectOptions &&
                          field.selectOptions.type === 'SINGLE' &&
                          !field.selectOptions.renderAsSelect;

                        if (isRadioGroup) {
                          return (
                            <RadioGroup
                              key={field.name}
                              value={controllerField.value || ''}
                              onChange={controllerField.onChange}
                            >
                              {field.selectOptions.options.map((option) => (
                                <FormControlLabel
                                  key={option.value}
                                  control={<Radio value={option.value} />}
                                  label={option.label}
                                  checked={option.value === controllerField.value}
                                />
                              ))}
                            </RadioGroup>
                          );
                        }

                        const isCheckboxGroup =
                          field.selectOptions && field.selectOptions.type === 'MULTIPLE';

                        if (isCheckboxGroup) {
                          return (
                            <CheckboxGroup
                              options={field.selectOptions.options}
                              selectedValues={
                                Array.isArray(controllerField.value) ? controllerField.value : []
                              }
                              setSelectedValues={controllerField.onChange}
                              sxOverrides={{
                                '& .MuiFormControlLabel-root': {
                                  marginRight: '0px',
                                },
                              }}
                            />
                          );
                        }

                        if (variant === 'otp') {
                          return (
                            <OtpInput error={error?.message} controllerField={controllerField} />
                          );
                        }

                        const hasMaxLength = field.rules?.maxLength;
                        const length = controllerField.value?.length || 0;
                        const isOverLimit = hasMaxLength
                          ? length > (field.rules?.maxLength || 0)
                          : false;

                        // Default TextField (no selectOptions)
                        return (
                          <BackrsFormTextField
                            field={field}
                            groupedFields={groupedFields}
                            index={index}
                            controllerField={controllerField}
                            error={isOverLimit ? String(isOverLimit) : error?.message}
                            isDisabled={isDisabled || false}
                          />
                        );
                      }}
                    />
                    {field.divider && (
                      <Divider sx={({ backrs }) => ({ mt: backrs.spacing['spacing-xl'] })}>
                        {field.dividerText}
                      </Divider>
                    )}
                  </Fragment>
                );
              })}
            </Box>
          );
        })}
        {buttonLabel && variant === 'otp' && renderFormButton(buttonLabel)}
        {children}
        {buttonLabel && variant === 'default' && renderFormButton(buttonLabel)}
      </Box>
    </FormProvider>
  );
};

const loadingSpinnerStyle: SxProps<Theme> = {
  width: 20,
  height: 20,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
};

const buttonContainerStyle: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
};

const groupContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-md'],
});
